import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Navbar from './components/Navbar';
import ModernHome from './components/ModernHome';
import CategoryDetailView from './components/CategoryDetailView';
import Testimonials from './components/Testimonials';
import Gallery from './components/Gallery';
import FamilyShowcase from './components/FamilyShowcase';
import BusinessShowcase from './components/BusinessShowcase';
import Contact from './components/Contact';
import Footer from './components/Footer';
import AboutUs from './components/AboutUs';
import DataWarningPopup from './components/DataWarningPopup';

function useScrollReveal(view, homeMode) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const timer = setTimeout(() => {
      const targets = document.querySelectorAll(
        '.reveal, .reveal-left, .reveal-right, .reveal-scale'
      );
      targets.forEach((el) => observer.observe(el));
    }, 100);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [view, homeMode]);
}

function App() {
  const [view, setView] = useState('home');
  const [homeMode, setHomeMode] = useState('family');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [galleryFilter, setGalleryFilter] = useState('All');
  const [pageKey, setPageKey] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const mainRef = useRef(null);

  useScrollReveal(view, homeMode);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setView('category');
    setPageKey(k => k + 1);
    window.scrollTo(0, 0);
  };

  const handleNavigate = (newView) => {
    setView(newView);
    setSelectedCategory(null);
    setGalleryFilter('All');
    setPageKey(k => k + 1);
    window.scrollTo(0, 0);
  };

  // Mobile वर bottom-right, Desktop वर top-right
  const socialContainerStyle = {
    position: 'fixed',
    zIndex: 9999,
    display: 'flex',
    ...(isMobile ? {
      bottom: '16px',
      right: '8px',
      top: 'auto',
      flexDirection: 'column',
      gap: '6px',
    } : {
      top: '20px',
      right: '20px',
      flexDirection: 'row',
      gap: '10px',
    })
  };

  const iconStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '10px',
    background: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    ...(isMobile ? {
      width: '34px',
      height: '34px',
      padding: '5px',
    } : {
      width: '42px',
      height: '42px',
      padding: '6px',
    })
  };

  return (
    <div className="App">
      {view !== 'home' && <Navbar onNavigate={handleNavigate} currentView={view} />}
      <main ref={mainRef}>
        <div key={pageKey} className="page-enter">
          {view === 'category' && selectedCategory ? (
            <CategoryDetailView
              category={selectedCategory}
              onBack={() => { setView('home'); setPageKey(k => k + 1); }}
              onViewMore={(filterName) => {
                setGalleryFilter(filterName);
                setView('gallery');
                setPageKey(k => k + 1);
                window.scrollTo(0, 0);
              }}
            />
          ) : view === 'contact' ? (
            <Contact />
          ) : view === 'about' ? (
            <AboutUs />
          ) : view === 'gallery' ? (
            <Gallery initialFilter={galleryFilter} />
          ) : (
            <>
              <ModernHome
                onNavigate={handleNavigate}
                onCategoryClick={handleCategoryClick}
                mode={homeMode}
                setMode={setHomeMode}
              />
              {homeMode === 'family' ? (
                <FamilyShowcase onCategoryClick={handleCategoryClick} />
              ) : (
                <BusinessShowcase onCategoryClick={handleCategoryClick} />
              )}
              <Testimonials />
            </>
          )}
        </div>
      </main>

      {/* Fixed Social Icons — सगळ्या pages वर */}
      <div style={socialContainerStyle}>
        <a href="https://wa.me/919762470338?text=Hi%2C%20I'm%20interested%20in%20your%20photography%20services!"
          target="_blank" rel="noopener noreferrer" style={iconStyle}>
          <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" style={{ width: '100%', height: '100%' }} />
        </a>
        <a href="https://www.facebook.com/profile.php?id=61577936123573"
          target="_blank" rel="noopener noreferrer" style={iconStyle}>
          <img src="https://upload.wikimedia.org/wikipedia/commons/b/b8/2021_Facebook_icon.svg" alt="Facebook" style={{ width: '100%', height: '100%' }} />
        </a>
        <a href="https://www.instagram.com/nikhildeshpande_shreephotos/"
          target="_blank" rel="noopener noreferrer" style={iconStyle}>
          <img src="https://upload.wikimedia.org/wikipedia/commons/e/e7/Instagram_logo_2016.svg" alt="Instagram" style={{ width: '100%', height: '100%' }} />
        </a>
        <a href="https://www.youtube.com/@NikhilsShreePhotos"
          target="_blank" rel="noopener noreferrer" style={iconStyle}>
          <img src="https://upload.wikimedia.org/wikipedia/commons/0/09/YouTube_full-color_icon_%282017%29.svg" alt="YouTube" style={{ width: '100%', height: '100%' }} />
        </a>
      </div>

      <Footer />
      <DataWarningPopup />
    </div>
  );
}

export default App;